/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { CompletionMessage } from '@app-libs/model';
import { Observable, from, throwError, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { FilesApiService } from '../../api/files-api.service';
import { MessagesApiService } from '../../api/messages-api.service';
import { AuthService } from '../../auth/auth.service';
import { TokenUsage } from '../chat-api-service.interface';
import { ChatStateService } from '../chat-state.service';
import { ConversationManager } from '../conversation/conversation-manager.service';
import { ConversationTitleService } from '../conversation/conversation-title.service';
import {
  IMessageManager,
} from './message.interfaces';

/**
 * MessageManager - Handles message CRUD operations
 *
 * This service handles all message operations, migrated from ChatApiService
 * with the exact same logic to preserve functionality.
 */
@Injectable({
  providedIn: 'root',
})
export class MessageManager implements IMessageManager {
  constructor(
    private messagesApiService: MessagesApiService,
    private filesApiService: FilesApiService,
    private authService: AuthService,
    private chatStateService: ChatStateService,
    private conversationManager: ConversationManager,
    private conversationTitleService: ConversationTitleService,
  ) { }

  /**
   * Create a user message
   * (Migrated from ChatApiService.createUserMessage - exact same logic)
   */
  createUserMessage(message: Partial<CompletionMessage>): Observable<CompletionMessage> {
    return from(this.ensureConversationExists(message.content || '')).pipe(
      switchMap(() => {
        const userId = this.authService.getCurrentUserId();
        if (!userId) {
          throw new Error('User not authenticated');
        }
        return from(Promise.resolve(userId));
      }),
      switchMap((userId) =>
        from(this.authService.getProfile()).pipe(
          map((profile) => ({
            ...message,
            conversation_id: this.chatStateService.conversation.conversation_id || null,
            user_id: userId,
            role: 'user' as const,
            workspace_id: profile?.selected_workspace || null,
            created_at: new Date().toISOString(),
            uploaded_file: message.uploaded_file, // Explicitly preserve the uploaded_file
          })),
        ),
      ),
      switchMap((messageData) => this.saveMessageWithFile(messageData)),
      catchError((error) => {
        console.error('Failed to create user message:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Create an assistant message
   * (Migrated from ChatApiService.createAssistantMessage - exact same logic)
   */
  createAssistantMessage(usage?: TokenUsage, toolCalls?: any[]): Observable<CompletionMessage> {
    console.debug('MessageManager: Creating assistant message with tool calls:', toolCalls);
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      return throwError(() => new Error('User not authenticated'));
    }

    return from(Promise.resolve(userId)).pipe(
      switchMap((userId) =>
        from(this.authService.getProfile()).pipe(
          map((profile) => ({
            conversation_id: this.chatStateService.conversation.conversation_id,
            user_id: userId,
            created_at: new Date().toISOString(),
            content: this.chatStateService.currentBotMessage,
            role: 'assistant' as const,
            status: 'completed',
            workspace_id: profile?.selected_workspace || null,
            tokens: usage?.total_tokens,
            completion_tokens: usage?.completion_tokens,
            total_tokens: usage?.total_tokens,
            cached_tokens: usage?.cached_tokens,
            prompt_tokens: usage?.prompt_tokens,
            metadata: toolCalls && toolCalls.length > 0 ? { tool_calls: toolCalls } : {},
          })),
        ),
      ),
      switchMap((messageData) => this.messagesApiService.createMessage(messageData as CompletionMessage)),
      switchMap((savedMessage) => {
        // Update conversation with last message date and potentially new ai generated title
        return this.conversationTitleService
          .generateTitle(this.chatStateService.messages as CompletionMessage[])
          .pipe(
            switchMap((title) => {
              console.log('Title generation result:', title);
              return this.conversationManager.updateConversation(
                this.chatStateService.conversation.conversation_id!,
                {
                  last_message_date: savedMessage.created_at,
                  ...(title && { title }),
                },
              );
            }),
            catchError((error) => {
              console.error('Failed to update conversation after title generation:', error);
              // Still update conversation with last_message_date even if title generation fails
              return this.conversationManager.updateConversation(
                this.chatStateService.conversation.conversation_id!,
                {
                  last_message_date: savedMessage.created_at,
                },
              );
            }),
            tap(() => console.log('Conversation updated successfully')),
            catchError((error) => {
              console.error('Final conversation update failed:', error);
              // Don't let conversation update failure stop the message creation
              return of(null);
            }),
            map(() => savedMessage) // Return the original message
          );
      }),
      catchError((error) => {
        console.error('Failed to create assistant message:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Update an existing message
   * (Migrated from ChatApiService.updateMessage - exact same logic)
   */
  updateMessage(messageId: string, updates: Partial<CompletionMessage>): Observable<void> {
    // Note: MessagesApiService doesn't have updateMessage method
    // This would need to be implemented in the backend first
    return throwError(() => new Error('Message update not implemented'));
  }

  /**
   * Save message with file attachment
   * (Migrated from ChatApiService.saveMessageWithFile - exact same logic)
   */
  private saveMessageWithFile(message: Partial<CompletionMessage>): Observable<CompletionMessage> {
    if (!message.uploaded_file) {
      return this.messagesApiService.createMessage(message as CompletionMessage);
    }

    // First create the file
    console.debug('Creating file for message:', message.uploaded_file);
    return this.filesApiService.createFile(message.uploaded_file).pipe(
      switchMap((savedFile) => {
        // Update message with file reference (preserve uploaded_file for local use, set file_id for DB)
        message.uploaded_file!.file_id = savedFile.file_id;
        message.file_id = savedFile.file_id;

        return this.messagesApiService.createMessage(message as CompletionMessage);
      }),
      catchError((error) => {
        console.error('Failed to save file:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Delete a message
   */
  deleteMessage(messageId: string): Observable<void> {
    // Not implemented in original ChatApiService
    return throwError(() => new Error('Message deletion not implemented'));
  }

  /**
   * Get messages for a conversation
   */
  getMessages(conversationId: string, limit?: number): Observable<CompletionMessage[]> {
    return this.messagesApiService.fetchMessagesByConversation(conversationId, limit).pipe(
      catchError((error) => {
        console.error('Failed to get messages:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Ensures a conversation exists before creating messages
   * (Helper method from ChatApiService - exact same logic)
   */
  private async ensureConversationExists(content: string): Promise<void> {
    if (!this.chatStateService.conversation.conversation_id) {
      await this.conversationManager
        .createConversation({
          initialMessage: content,
          workspaceId: this.chatStateService.workspaceId,
          assistantId: this.chatStateService.assistant.assistant_id,
        })
        .toPromise();
    }
  }
}
